import { useCallback, useMemo, useState } from "react";

import type { CdropdownOption } from "./Cdropdown.types";
import { findFirstEnabledIndex } from "./option-state";

interface UseCdropdownStateInput {
  controlledIsOpen: boolean | undefined;
  onOpenChange: ((isOpen: boolean) => void) | undefined;
  controlledActiveIndex: number | null | undefined;
  onActiveIndexChange: ((index: number) => void) | undefined;
  options: CdropdownOption[];
}

interface UseCdropdownStateResult {
  effectiveActiveIndex: number | null;
  hoveredIndex: number | null;
  internalActiveIndex: number | null;
  isActiveIndexControlled: boolean;
  isOpen: boolean;
  setActiveIndex: (index: number | null) => void;
  setHoveredIndex: (index: number | null) => void;
  setInternalActiveIndex: (index: number | null) => void;
  setIsOpen: (isOpen: boolean) => void;
}

function isSelectableOptionIndex(input: {
  index: number | null;
  options: CdropdownOption[];
}): input is {
  index: number;
  options: CdropdownOption[];
} {
  if (input.index == null) {
    return false;
  }

  return (
    input.index >= 0 &&
    input.index < input.options.length &&
    !input.options[input.index]?.disabled
  );
}

function resolveEffectiveActiveIndex(input: {
  options: CdropdownOption[];
  resolvedActiveIndex: number | null;
}): number | null {
  if (
    isSelectableOptionIndex({
      index: input.resolvedActiveIndex,
      options: input.options,
    })
  ) {
    return input.resolvedActiveIndex;
  }

  const firstEnabledIndex = findFirstEnabledIndex(input.options);
  return firstEnabledIndex === -1 ? null : firstEnabledIndex;
}

export function useCdropdownState({
  controlledIsOpen,
  onOpenChange,
  controlledActiveIndex,
  onActiveIndexChange,
  options,
}: UseCdropdownStateInput): UseCdropdownStateResult {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [internalActiveIndex, setInternalActiveIndex] = useState<number | null>(
    null,
  );

  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const isActiveIndexControlled = controlledActiveIndex !== undefined;
  const resolvedActiveIndex = isActiveIndexControlled
    ? controlledActiveIndex
    : internalActiveIndex;

  const effectiveActiveIndex = useMemo(() => {
    return resolveEffectiveActiveIndex({ options, resolvedActiveIndex });
  }, [options, resolvedActiveIndex]);

  const setIsOpen = useCallback(
    (newIsOpen: boolean) => {
      if (controlledIsOpen !== undefined) {
        onOpenChange?.(newIsOpen);
        return;
      }

      setInternalIsOpen(newIsOpen);
    },
    [controlledIsOpen, onOpenChange],
  );

  const setActiveIndex = useCallback(
    (index: number | null) => {
      if (!isActiveIndexControlled) {
        setInternalActiveIndex(index);
      }

      if (index !== null) {
        onActiveIndexChange?.(index);
      }
    },
    [isActiveIndexControlled, onActiveIndexChange],
  );

  return {
    effectiveActiveIndex,
    hoveredIndex,
    internalActiveIndex,
    isActiveIndexControlled,
    isOpen,
    setActiveIndex,
    setHoveredIndex,
    setInternalActiveIndex,
    setIsOpen,
  };
}
